import { useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Check } from "lucide-react";
import { PageLoader } from "@/components/ui/Spinner";
import { EmptyState, TableShell, Th } from "@/features/power_projects/components/ui";
import { useCompleteTask, useLeadTasks } from "@/features/leads/api/leads";

/**
 * Zadania pozyskaniowe w poprzek wszystkich leadów — „co mam dziś zrobić".
 *
 * Osobna zakładka, bo praca w CRM zaczyna się od tego pytania, a nie od
 * otwierania kart po kolei.
 */

export function LeadTasksPage() {
  const [mineOnly, setMineOnly] = useState(true);
  const [openOnly, setOpenOnly] = useState(true);
  const { data: tasks, isLoading } = useLeadTasks({
    assigned: mineOnly ? "me" : undefined,
    open: openOnly,
  });
  const complete = useCompleteTask();

  const rows = tasks ?? [];
  const overdue = rows.filter((task) => task.is_overdue).length;

  return (
    <>
      <div className="card mb-5 flex flex-wrap items-center gap-4 p-3">
        <label className="section-label flex items-center gap-1.5">
          <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} />
          Tylko moje
        </label>
        <label className="section-label flex items-center gap-1.5">
          <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
          Tylko niewykonane
        </label>
        <span
          className={
            overdue > 0
              ? "font-mono text-xs font-bold text-[var(--color-primary)]"
              : "section-label"
          }
        >
          {overdue > 0 ? `Po terminie: ${overdue}` : "Nic po terminie"}
        </span>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : rows.length === 0 ? (
        <div className="card">
          <EmptyState>Brak zadań.</EmptyState>
        </div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th className="w-10" />
              <Th>Zadanie</Th>
              <Th>Lead</Th>
              <Th>Termin</Th>
              <Th>Odpowiedzialny</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((task) => (
              <tr key={task.id} className="border-t border-[var(--color-border)]">
                <td className="px-3 py-2.5">
                  {!task.is_done && (
                    <button
                      type="button"
                      title="Oznacz jako wykonane"
                      onClick={() => complete.mutate({ id: task.id })}
                      className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                    >
                      <Check size={14} />
                    </button>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <span
                    className={
                      task.is_done
                        ? "text-[var(--color-text-muted)] line-through"
                        : "text-[var(--color-text-primary)]"
                    }
                  >
                    {task.title}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <Link to={`/leads/${task.lead}`} className="hover:text-[var(--color-primary)]">
                    {task.lead_name}
                  </Link>
                </td>
                <td
                  className={
                    task.is_overdue
                      ? "px-3 py-2.5 font-mono text-[var(--color-primary)]"
                      : "px-3 py-2.5 font-mono text-[var(--color-text-secondary)]"
                  }
                >
                  {format(new Date(task.due_at), "dd.MM.yyyy HH:mm")}
                  {task.is_overdue && " · po terminie"}
                </td>
                <td className="px-3 py-2.5 text-[var(--color-text-secondary)]">
                  {task.assigned_to_name ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </TableShell>
      )}
    </>
  );
}
